import React, { useEffect, useState } from 'react'
import { Moment } from 'moment'
import Modal from '../../shared/modal'
import PlusMinusWrapper from '../../shared/plusMinusWrapper'
import { t } from '../../../i18n'
import axios from 'axios'
import moment from 'moment'
import Loading from '../../shared/loading'

const scope = 'replenishment'

export interface MenuRecipe {
  id: number
  menu_id: number
  recipe_id: number
  recipe_name: string
  amount: number
}

export interface Menu {
  id: number
  date: string
  menu_recipes: MenuRecipe[]
}

export interface Recipe {
  id: number
  name: string
  file_url: string | null
  average_orders_per_day: number
  product_ids: number[]
}

interface ViewMenuModalProps {
  date: Moment
  menuForDate: Menu | null
  setMenuForDate: React.Dispatch<React.SetStateAction<Menu | null>>
  onClose: () => void
  filterProductId?: number | null  // Product ID to filter recipes by
  title?: string  // Custom title override for filtered views
  setFlashMessage: (type: string, message: string) => void
  onCartRefresh: () => void
}

const ViewMenuModal = ({
  date,
  menuForDate,
  setMenuForDate,
  onClose,
  filterProductId,
  title,
  setFlashMessage,
  onCartRefresh
}: ViewMenuModalProps) => {
  const formattedDate = date.format('DD/MM')

  const [loading, setLoading] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [editedMenuRecipes, setEditedMenuRecipes] = useState<MenuRecipe[]>([])
  const [originalAmounts, setOriginalAmounts] = useState<Map<number, number>>(new Map())

  const getRecipeById = (recipeId: number): Recipe | undefined => {
    return recipes.find(r => r.id === recipeId)
  }

  const headerText = title || `${t('sidebar.menu_modal.title', { scope })} ${formattedDate}`

  useEffect(() => {
    if (menuForDate) {
      let menuRecipesToUse = menuForDate.menu_recipes
      if (filterProductId) {
        menuRecipesToUse = menuForDate.menu_recipes.filter(mr => {
          const recipe = recipes.find(r => r.id === mr.recipe_id)
          return recipe?.product_ids.includes(filterProductId)
        })
      }
      setEditedMenuRecipes(menuRecipesToUse)

      // Initialize original amounts for change detection
      const amounts = new Map<number, number>()
      menuRecipesToUse.forEach(mr => amounts.set(mr.id, mr.amount))
      setOriginalAmounts(amounts)
    } else {
      setEditedMenuRecipes([])
      setOriginalAmounts(new Map())
    }
  }, [menuForDate, filterProductId, recipes])

  // Fetch recipes on mount
  useEffect(() => {
    const abortController = new AbortController()

    const fetchRecipes = async () => {
      try {
        setLoading(true)
        const response = await axios.get(Routes.api_internal_recipes_path(), {
          signal: abortController.signal
        })
        setRecipes(response.data)
        setLoading(false)
      } catch (error) {
        if (axios.isCancel(error)) {
          return
        }
        console.error('Error fetching recipes:', error)
        setLoading(false)
      }
    }
    fetchRecipes()

    return () => {
      abortController.abort()
    }
  }, [])

  const saveMenu = async () => {
    if (!menuForDate || !date) return

    setSaving(true)

    try {
      // Update only changed menu recipes
      const updatePromises = editedMenuRecipes
        .filter(mr => mr.amount !== originalAmounts.get(mr.id))
        .map(mr =>
          axios.patch(Routes.api_internal_menu_recipe_path(mr.id), {
            menu_recipe: { amount: mr.amount }
          })
        )

      await Promise.all(updatePromises)

      // Refresh menu data
      const monthString = moment(date).format('YYYY-M')
      const response = await axios.get(Routes.within_month_api_internal_menus_path(monthString))
      const menus: Menu[] = response.data
      const updatedMenu = menus.find(m => m.date === moment(date).format('YYYY-MM-DD'))
      setMenuForDate(updatedMenu || null)

      // Regenerate stock order
      await axios.post('/api/internal/replenishment_orders', { menu_date: date.format('YYYY-MM-DD') })

      onClose()
    } catch (error: any) {
      console.error('Error saving menu:', error)
      setFlashMessage('danger', error.response?.data?.error || 'Error saving menu')
    } finally {
      onCartRefresh()
      setSaving(false)
    }
  }

  const handleRecipeAmountChange = (index: number, value: number) => {
    const newValue = Math.max(0, value)
    const updated = [...editedMenuRecipes]
    updated[index] = { ...updated[index], amount: newValue }
    setEditedMenuRecipes(updated)
  }

  const handleDeleteRecipe = async (index: number) => {
    const menuRecipe = editedMenuRecipes[index]
    if (!menuRecipe?.id) return

    try {
      await axios.delete(Routes.api_internal_menu_recipe_path(menuRecipe.id))

      // Update local state
      const updated = editedMenuRecipes.filter((_, i) => i !== index)
      setEditedMenuRecipes(updated)

      // Update parent state
      if (menuForDate) {
        setMenuForDate({
          ...menuForDate,
          menu_recipes: menuForDate.menu_recipes.filter(mr => mr.id !== menuRecipe.id)
        })
      }

      // Regenerate stock order
      await axios.post('/api/internal/replenishment_orders', { menu_date: date.format('YYYY-MM-DD') })
      onCartRefresh()
    } catch (error: any) {
      console.error('Error deleting menu recipe:', error)
      setFlashMessage('danger', error.response?.data?.error || 'Error deleting recipe')
    }
  }

  const renderLoading = () => loading && <Loading position="center" />

  return (
    <Modal
      onClose={onClose}
      headerText={headerText}
      widen={true}
    >
      <div className="view-menu-modal-content">
        {editedMenuRecipes.length === 0 ? (
          <div className="empty-menu-message">
            {renderLoading()}
            <span>{!loading && t('sidebar.menu_modal.no_recipes_with_ingredient', { scope })}</span>
          </div>
        ) : (
          editedMenuRecipes.map((menuRecipe, index) => {
            const recipe = getRecipeById(menuRecipe.recipe_id)
            return (
              <div key={`${menuRecipe.recipe_id}-${index}`} className={`menu-recipe-item d-flex align-items-center mb-3 index-${index}`}>
                <div className="recipe-image">
                  {recipe?.file_url ? (
                    <img src={recipe.file_url} alt={menuRecipe.recipe_name} />
                  ) : (
                    <div className="recipe-image-placeholder" />
                  )}
                </div>
                <div className="recipe-info">
                  <span className="recipe-name">{menuRecipe.recipe_name}</span>
                  <span className="recipe-system">
                    {t('sidebar.menu_modal.system', { scope })}: {Math.round(recipe?.average_orders_per_day || 0)}
                  </span>
                </div>
                <div className="recipe-controls">
                  <PlusMinusWrapper
                    className='ms-3'
                    value={menuRecipe.amount}
                    step={1}
                    onChange={(_, value) => handleRecipeAmountChange(index, value)}
                  >
                    <div className="subwrapper">
                      <input
                        type='number'
                        name='amount'
                        value={menuRecipe.amount}
                        min="0"
                        onChange={(e) => handleRecipeAmountChange(index, Number(e.target.value))}
                      />
                    </div>
                  </PlusMinusWrapper>
                </div>
                <div className="recipe-delete">
                  <i
                    className="button-delete fa fa-trash-o fa-2x ms-3 pointer"
                    onClick={() => handleDeleteRecipe(index)}
                  />
                </div>
              </div>
            )
          })
        )}
      </div>
      <div className="view-menu-modal-footer modal-footer me-auto">
        <button className="btn btn-primary" onClick={saveMenu} disabled={saving}>
          {saving ? <i className="fa fa-spinner fa-spin" /> : t('sidebar.menu_modal.save_menu', { scope })}
        </button>
      </div>
    </Modal>
  )
}

export default ViewMenuModal
